/**
 * Gallery scene: in-document AI edit review (?test=maater-ui#diff-review)
 *
 * Art until Rowan locks. Default path is a block-level unified diff on
 * reading prose (TipTap chrome), not the blocking Split/Unified modal.
 * Source compare stays opt-in for a large rewrite.
 *
 * Mapping hypothesis: a markdown line range paints the TipTap block that
 * contains it. Lists and tables wash the whole block. Character-level
 * marks and a real line-to-node map are deferred.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Check, ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react'
import { toast } from 'sonner'
import { ListSurfaceStatusBar } from '@/components/ui/list-surface-status-bar'
import { Toaster } from '@/components/ui/toaster'
import { ProtoTitleRow } from '@/components/prototype/maater-proto-chrome'
import { UnifiedDiffView } from '@/components/features/unified-diff-view'
import { useDiffComputation } from '@/hooks/use-diff-computation'
import { changeIncludesLine } from '@/lib/diff-overview-markers'
import { placeReviewChips } from '@/lib/review-chip-place'
import { testIds } from '@/lib/test-ids'
import { QUIET_TOAST_DURATION_MS, quietToastOptions } from '@/lib/toast-chrome'
import { cn } from '@/lib/utils'
import '@/components/features/document-reading-prose.css'
import '@/components/prototype/maater-ui-diff-review-scene.css'

type ReviewMode = 'document' | 'batch' | 'compare'
type Decision = 'pending' | 'accepted' | 'rejected' | 'editing'
type BlockKind = 'same' | 'add' | 'remove'
type BlockNode = 'h1' | 'h2' | 'p' | 'ul'

type ReviewBlock = {
  id: string
  kind: BlockKind
  node: BlockNode
  text?: string
  items?: string[]
  /** Blocks that share an id are one hunk (a replace is remove + add). */
  hunkId?: string
  /** Paragraph the production Review scroll tends to stop on. */
  decoy?: boolean
}

type ReviewFile = {
  id: string
  title: string
  crumb: string
  blocks: ReviewBlock[]
}

const MODES: { id: ReviewMode; label: string }[] = [
  { id: 'document', label: 'In document' },
  { id: 'batch', label: 'Batch' },
  { id: 'compare', label: 'Source compare' },
]

const REID_BLOCKS: ReviewBlock[] = [
  { id: 'r-h', kind: 'same', node: 'h1', text: 'Reid Patterson' },
  {
    id: 'r-rel',
    kind: 'same',
    node: 'p',
    text: 'Relationship: Director, Kootenay Lightweb (elected 2026-10-24). Leads the Lightweb ERP Working Group. Symposium speaker. Rowan has not been introduced.',
  },
  {
    id: 'r-status',
    kind: 'same',
    node: 'p',
    text: 'Status: No direct Maater contact on file. Known from Dan Nelson\'s symposium recap and a thin public listing.',
  },
  {
    id: 'r-loc',
    kind: 'same',
    node: 'p',
    text: 'Location: Nelson, BC, on a public listing. Not confirmed in person.',
  },
  { id: 'r-qf', kind: 'same', node: 'h2', text: 'Quick facts' },
  {
    id: 'r-who',
    kind: 'same',
    node: 'p',
    text: 'One of two directors elected at the 2026-10-24 AGM. The recap names Cognitive Systems at UBC, installation art, and local 3D work through EMIT Studio. A LinkedIn snippet mentions Thaumazo. Do not treat that as confirmed.',
  },
  {
    id: 'r-connect',
    kind: 'same',
    node: 'p',
    text: 'Same symposium as Rowan. Dustin welcomed him and Jennifer Rebbetoy as system-oriented board additions.',
  },
  {
    id: 'r-said',
    kind: 'same',
    node: 'h2',
    text: 'What he said',
    decoy: true,
  },
  {
    id: 'r-src',
    kind: 'same',
    node: 'p',
    text: 'Source: Dan Nelson\'s 2026-10-24 symposium recap. Creative people and small businesses are buried in disorganization and priced out of software that costs tens of thousands in licences.',
  },
  {
    id: 'r-const',
    kind: 'same',
    node: 'p',
    text: 'His line: software encodes values. Writing it is like slowly writing a constitution we will all live under.',
  },
  {
    id: 'r-coop',
    kind: 'same',
    node: 'p',
    text: 'He is leading a shared, open-source business system for Kootenay businesses, funded and owned together as a co-op.',
  },
  {
    id: 'r-room',
    kind: 'same',
    node: 'p',
    text: 'The room was the FLFE Innovations space in Nelson. Dustin introduced the new directors as people who think in systems, not only in programs.',
  },
  {
    id: 'r-slide',
    kind: 'same',
    node: 'p',
    text: 'The slideshow is public. It walks through licence cost, burnout, and the co-op as the owner of the system. That deck is the citation.',
  },
  {
    id: 'r-odin',
    kind: 'same',
    node: 'p',
    text: 'Nothing in the recap is a commitment from Reid to Maater. He did not ask for a follow-up. The notes below are what Dan wrote down, not a transcript.',
  },
  {
    id: 'r-after',
    kind: 'same',
    node: 'p',
    text: 'A few people stayed after for the ERP working group. Those notes are not in the public deck. Treat them as unconfirmed until someone who was in the room says otherwise.',
  },
  {
    id: 'r-talk-old',
    kind: 'remove',
    node: 'p',
    hunkId: 'talk',
    text: 'Talk: Building Digital Business Infrastructure.',
  },
  {
    id: 'r-talk-new',
    kind: 'add',
    node: 'p',
    hunkId: 'talk',
    text: 'Talk, in his words: software encodes values, and writing it is like slowly writing a constitution. The public slideshow is the version to cite. Working-group notes from the same afternoon are not cleared to share.',
  },
  { id: 'r-why-h', kind: 'same', node: 'h2', text: 'Why he matters to Maater' },
  {
    id: 'r-why',
    kind: 'same',
    node: 'p',
    text: 'The ERP working group is the nearest neighbour to Maater, not a substitute. His frame is licences, burnout, and a co-op-owned system. Maater\'s frame is a team\'s working knowledge. Pitch them side by side.',
  },
  {
    id: 'r-steps',
    kind: 'add',
    node: 'ul',
    hunkId: 'steps',
    items: [
      'Ask Dan or Dustin for an intro before writing him cold.',
      'On intro: hear what the ERP group is scoping. Do not assume Odoo.',
      'Keep the offer distinct: memory and agent layer, not the business system.',
    ],
  },
]

const STEPHAN_BLOCKS: ReviewBlock[] = [
  { id: 's-h', kind: 'same', node: 'h1', text: 'Stephan Martineau' },
  {
    id: 's-rel',
    kind: 'same',
    node: 'p',
    text: 'Relationship: Manager, Slocan Integral Forestry Cooperative (SIFCo), as Dan Nelson described him. Symposium speaker. Not yet a Maater contact.',
  },
  {
    id: 's-talk',
    kind: 'same',
    node: 'p',
    text: 'Talk: Community Resilience, Technology and AI for Forest Management. Lightweb symposium, 2026-10-24, Nelson. Rowan has not been introduced.',
  },
  {
    id: 's-place',
    kind: 'same',
    node: 'p',
    text: 'Location: Slocan Valley, British Columbia. SIFCo is the community forest. Tenure is described as about 39,000 acres.',
  },
  { id: 's-said-h', kind: 'same', node: 'h2', text: 'What he said', decoy: true },
  {
    id: 's-eco',
    kind: 'same',
    node: 'p',
    text: 'Integral forestry sets the ecological limits of the land first, then plans human use inside those limits. They have led the valley on wildfire resilience and FireSmart education.',
  },
  {
    id: 's-ai',
    kind: 'same',
    node: 'p',
    text: 'He talked about where AI fits across mapping, monitoring, and planning. SIFCo is scoping its own local AI node. The Lightweb is looking at helping build it.',
  },
  {
    id: 's-dan',
    kind: 'same',
    node: 'p',
    text: 'Dan\'s line: community tech serving the land. The public about page confirms Stephan presented the guiding principles. It does not, in the section reviewed, give a current job title. Keep "manages" as Dan\'s description.',
  },
  {
    id: 's-fire',
    kind: 'same',
    node: 'p',
    text: 'The valley work he described is wildfire resilience, FireSmart education, and landscape-level fire planning. That is the operations picture, separate from the AI node.',
  },
  {
    id: 's-node',
    kind: 'same',
    node: 'p',
    text: 'SIFCo is scoping its own local node. The Lightweb may help build it. Matt Gregoire\'s hardware talk is the capacity picture: on the order of a thousand people, AI stays optional.',
  },
  {
    id: 's-room',
    kind: 'same',
    node: 'p',
    text: 'Rowan was in the symposium room and was not introduced. There is no email on file. Do not write from the recap alone.',
  },
  { id: 's-why-h', kind: 'same', node: 'h2', text: 'Why he matters to Maater' },
  {
    id: 's-fit-old',
    kind: 'remove',
    node: 'p',
    hunkId: 'claim',
    text: 'Maater ships on the SIFCo node as the memory layer for maps and plans.',
  },
  {
    id: 's-fit-new',
    kind: 'add',
    node: 'p',
    hunkId: 'claim',
    text: 'This is a concrete local-AI deployment, not a philosophy conversation. Maater would only enter if they want a memory layer the co-op can audit. Do not lead with the product.',
  },
  {
    id: 's-nda',
    kind: 'add',
    node: 'p',
    hunkId: 'nda',
    text: 'NDA context: treat the node scoping notes as confidential until Stephan confirms an intro. Dan\'s recap can stay internal. Do not forward the local-AI plan outside Lightweb.',
  },
  {
    id: 's-next',
    kind: 'same',
    node: 'p',
    text: 'Next: ask Dan who on the Lightweb side is actually scoping the node before Rowan writes. None of this is a direct email. There is no address on file.',
  },
]

const FILES: ReviewFile[] = [
  { id: 'reid', title: 'Reid Patterson', crumb: 'People', blocks: REID_BLOCKS },
  { id: 'stephan', title: 'Stephan Martineau', crumb: 'People', blocks: STEPHAN_BLOCKS },
]

/** Large-rewrite fixture for the opt-in source compare. Not the default path. */
const REWRITE_ORIGINAL = `# Reid Patterson

Director, Kootenay Lightweb. Symposium speaker.

## Quick facts

- Elected director on 2026-10-24
- Leads the ERP Working Group
- Talk: Building Digital Business Infrastructure
- LinkedIn snippet mentions Thaumazo (unconfirmed)

## What he said

Creative people are buried in disorganization and priced out of business software.

Software encodes values. Writing it is like slowly writing a constitution.

He wants a shared open-source business system, owned as a co-op.

## Why he matters

The ERP group is the nearest neighbour to Maater, not a substitute.

Pitch them side by side. Do not pitch Maater as the ERP.

## Next steps

- Ask Dan for an intro
- Hear what they are scoping
- Keep Maater's offer distinct
`

const REWRITE_PROPOSED = `# Reid Patterson

Director, Kootenay Lightweb, elected 2026-10-24. Leads the ERP Working Group. Not yet a Maater contact.

## Symposium

Talk title on the program: Building Digital Business Infrastructure.

What he actually argued: software encodes values, and writing it is like slowly writing a constitution the community will live under. The public slideshow is the version to cite.

Working-group notes from the same afternoon are not cleared to share.

## Unconfirmed

A LinkedIn search snippet calls him a part-time developer at Thaumazo and an educator now in Nelson. LinkedIn did not load. Do not treat Thaumazo, an email, or a personal site as fact.

## Why he matters to Maater

Nearest neighbour, not a substitute. His frame is operational software: licences, burnout, a co-op-owned system. Maater's frame is why a decision was made.

Dustin already asked about Odoo. That question now sits with Reid's group. Coordinate before any outreach.

As a new director he will be in the room when Lightweb talks about hosting on co-op hardware.

## Next

- Intro via Dan or Dustin. No cold email.
- Ask what they are scoping. Do not assume Odoo.
- Memory and agent layer. Not the ERP.
`

const TIPS =
  'Accept all keeps every change. Hover an edit for Accept and Reject on just that one. Click the green wording to edit it. Undo is on the toast.'

function hunkKey(fileId: string, hunkId: string) {
  return `${fileId}:${hunkId}`
}

const SETTLE_MS = 560
const WASH_OUT_MS = 720

type WashOut = { id: string; hunkId: string; tone: 'keep' | 'restore' }
type ScrollPin = { anchorId: string; anchorTop: number; scrollTop: number }

function washesFor(
  blocks: ReviewBlock[],
  hunkId: string | undefined,
  kind: 'accepted' | 'rejected',
): WashOut[] {
  const tone = kind === 'accepted' ? 'keep' : 'restore'
  return blocks
    .filter((block) => {
      if (!block.hunkId || block.kind === 'same') return false
      if (hunkId && block.hunkId !== hunkId) return false
      return kind === 'accepted' ? block.kind === 'add' : block.kind === 'remove'
    })
    .map((block) => ({ id: block.id, hunkId: block.hunkId ?? '', tone }))
}

function reviewToast(kind: 'accepted' | 'rejected', title: string, undo: () => void) {
  const kept = kind === 'accepted'
  const fire = kept ? toast.success : toast.info
  fire(
    kept ? 'Kept the new wording' : 'Restored the earlier wording',
    quietToastOptions(kept ? 'success' : 'info', {
      id: 'diff-review-undo',
      description: title,
      duration: QUIET_TOAST_DURATION_MS.withAction,
      action: {
        label: 'Undo',
        onClick: () => {
          undo()
          toast('Undone', quietToastOptions('success', { id: 'diff-review-undo' }))
        },
      },
    }),
  )
}

function applyDecision(block: ReviewBlock, decision: Decision): ReviewBlock[] {
  if (decision === 'accepted') {
    if (block.kind === 'remove') return []
    return [{ ...block, kind: 'same', hunkId: undefined }]
  }
  if (decision === 'rejected') {
    if (block.kind === 'add') return []
    return [{ ...block, kind: 'same', hunkId: undefined }]
  }
  return [block]
}

function presentBlocks(
  blocks: ReviewBlock[],
  fileId: string,
  fileDecision: Decision,
  hunkDecisions: Record<string, Decision>,
): ReviewBlock[] {
  if (fileDecision === 'accepted' || fileDecision === 'rejected') {
    return blocks.flatMap((block) =>
      block.hunkId && block.kind !== 'same' ? applyDecision(block, fileDecision) : [block],
    )
  }
  return blocks.flatMap((block) => {
    if (!block.hunkId || block.kind === 'same') return [block]
    return applyDecision(block, hunkDecisions[hunkKey(fileId, block.hunkId)] ?? 'pending')
  })
}

function hunkIdsInOrder(blocks: ReviewBlock[]): string[] {
  const ids: string[] = []
  for (const block of blocks) {
    if (block.hunkId && block.kind !== 'same' && !ids.includes(block.hunkId)) {
      ids.push(block.hunkId)
    }
  }
  return ids
}

function chunkBlocks(blocks: ReviewBlock[]): { id: string; hunkId?: string; blocks: ReviewBlock[] }[] {
  const chunks: { id: string; hunkId?: string; blocks: ReviewBlock[] }[] = []
  for (const block of blocks) {
    const prev = chunks[chunks.length - 1]
    const hunkId = block.hunkId && block.kind !== 'same' ? block.hunkId : undefined
    if (hunkId && prev?.hunkId === hunkId) {
      prev.blocks.push(block)
      continue
    }
    chunks.push({ id: block.id, hunkId, blocks: [block] })
  }
  return chunks
}

function scrollTargetIntoScroller(
  scroller: HTMLElement,
  target: HTMLElement,
  align: 'center' | 'start',
  behavior: ScrollBehavior = 'auto',
) {
  const delta = target.getBoundingClientRect().top - scroller.getBoundingClientRect().top
  const next =
    align === 'center'
      ? scroller.scrollTop + delta - (scroller.clientHeight - target.getBoundingClientRect().height) / 2
      : scroller.scrollTop + delta - 12
  scroller.scrollTo({ top: Math.max(0, next), behavior })
}

function BlockBody({
  block,
  editable,
  onFocus,
  onBlur,
}: {
  block: ReviewBlock
  editable: boolean
  onFocus?: () => void
  onBlur?: () => void
}) {
  const edit = editable
    ? {
        contentEditable: true as const,
        suppressContentEditableWarning: true,
        className: 'outline-none',
        onMouseDown: onFocus,
        onFocus,
        onBlur,
      }
    : undefined
  if (block.node === 'h1') return <h1 {...edit}>{block.text}</h1>
  if (block.node === 'h2') return <h2 {...edit}>{block.text}</h2>
  if (block.node === 'ul') {
    return (
      <ul {...edit}>
        {(block.items ?? []).map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    )
  }
  return <p {...edit}>{block.text}</p>
}

function ReviewActions({
  acceptLabel,
  rejectLabel,
  onAccept,
  onReject,
  disabled,
  acceptTestId,
  rejectTestId,
  label,
}: {
  acceptLabel: string
  rejectLabel: string
  onAccept: () => void
  onReject: () => void
  disabled?: boolean
  acceptTestId: string
  rejectTestId: string
  label: string
}) {
  return (
    <div
      className="flex h-6 items-stretch overflow-hidden rounded-md border border-border/80 bg-background text-[12px] leading-none"
      role="group"
      aria-label={label}
    >
      <button
        type="button"
        disabled={disabled}
        className="px-2 font-medium text-primary transition-colors duration-150 hover:bg-primary/10 disabled:opacity-40"
        data-testid={acceptTestId}
        data-review-hover="accept"
        onMouseDown={(event) => event.preventDefault()}
        onClick={onAccept}
      >
        {acceptLabel}
      </button>
      <span className="w-px self-stretch bg-border/80" aria-hidden />
      <button
        type="button"
        disabled={disabled}
        className="px-2 text-muted-foreground transition-colors duration-150 hover:bg-muted/70 hover:text-foreground disabled:opacity-40"
        data-testid={rejectTestId}
        data-review-hover="reject"
        onMouseDown={(event) => event.preventDefault()}
        onClick={onReject}
      >
        {rejectLabel}
      </button>
    </div>
  )
}

function DecisionBanner({ decision }: { decision: Decision }) {
  if (decision === 'pending') return null
  const copy =
    decision === 'accepted'
      ? 'Accepted. The proposal is applied in this prototype. Nothing was written to the document.'
      : decision === 'rejected'
        ? 'Rejected. The proposal is discarded in this prototype. Nothing was written.'
        : 'You can type in the note. Accept keeps this wording. Reject puts the old wording back.'
  return (
    <p className="border-b border-border bg-muted/40 px-4 py-2 text-xs text-muted-foreground">
      {copy}
    </p>
  )
}

function SourceCompare({
  original,
  proposed,
  filename,
}: {
  original: string
  proposed: string
  filename: string
}) {
  const [view, setView] = useState<'split' | 'unified'>('unified')
  const scrollerRef = useRef<HTMLDivElement>(null)
  const { additions, deletions, changes, normalizedOriginal, normalizedProposed } =
    useDiffComputation(original, proposed, filename)
  const originalLines = normalizedOriginal.split('\n')
  const proposedLines = normalizedProposed.split('\n')

  useLayoutEffect(() => {
    const scroller = scrollerRef.current
    const first = scroller?.querySelector('[data-diff-change="true"]')
    if (scroller && first instanceof HTMLElement) {
      scrollTargetIntoScroller(scroller, first, 'center')
    }
  }, [view])

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid={testIds.maaterUiGallery.diffReviewCompare}>
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-2">
        <p className="min-w-0 flex-1 text-xs text-muted-foreground">
          Opt-in for a large rewrite. Today this compare is the blocking modal
          and the default. Here it is secondary.
        </p>
        <div
          className="inline-flex items-center rounded-md border border-border p-0.5"
          role="tablist"
          aria-label="Source compare view"
        >
          {(['split', 'unified'] as const).map((id) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={view === id}
              data-testid={testIds.maaterUiGallery.diffReviewCompareView(id)}
              className={cn(
                'rounded-sm px-2.5 py-1 text-xs capitalize',
                view === id
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
              onClick={() => setView(id)}
            >
              {id}
            </button>
          ))}
        </div>
        <span className="text-xs text-green-600 dark:text-green-400">+{additions}</span>
        <span className="text-xs text-red-600 dark:text-red-400">-{deletions}</span>
      </div>
      <div ref={scrollerRef} className="min-h-0 flex-1 overflow-auto bg-muted/20">
        {view === 'unified' ? (
          <UnifiedDiffView original={original} proposed={proposed} filename={filename} />
        ) : (
          <div className="flex min-h-full">
            <div className="min-w-0 flex-1 border-r border-border p-2">
              <p className="px-2 pb-2 text-xs text-muted-foreground">Original</p>
              {originalLines.map((line, index) => {
                const removed = changeIncludesLine(changes, 'removed', line)
                return (
                  <div
                    key={`o-${index}`}
                    data-diff-change={removed ? 'true' : undefined}
                    className={cn(
                      'border-l-2 px-2 py-0.5 font-mono text-xs',
                      removed
                        ? 'border-red-500/70 bg-red-500/10 text-red-900 dark:text-red-200'
                        : 'border-transparent text-foreground',
                    )}
                  >
                    {line || ' '}
                  </div>
                )
              })}
            </div>
            <div className="min-w-0 flex-1 p-2">
              <p className="px-2 pb-2 text-xs text-muted-foreground">Proposed</p>
              {proposedLines.map((line, index) => {
                const added = changeIncludesLine(changes, 'added', line)
                return (
                  <div
                    key={`p-${index}`}
                    data-diff-change={added ? 'true' : undefined}
                    className={cn(
                      'border-l-2 px-2 py-0.5 font-mono text-xs',
                      added
                        ? 'border-green-500/70 bg-green-500/10 text-green-950 dark:text-green-100'
                        : 'border-transparent text-foreground',
                    )}
                  >
                    {line || ' '}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export function DiffReviewScene() {
  const [mode, setMode] = useState<ReviewMode>('document')
  const [fileIndex, setFileIndex] = useState(0)
  const [fileDecisions, setFileDecisions] = useState<Record<string, Decision>>({})
  const [hunkDecisions, setHunkDecisions] = useState<Record<string, Decision>>({})
  const [compareDecision, setCompareDecision] = useState<Decision>('pending')
  const [hunkIndex, setHunkIndex] = useState(0)
  const [scrollIntent, setScrollIntent] = useState<{ kind: 'hunk' | 'decoy'; nonce: number }>({
    kind: 'hunk',
    nonce: 0,
  })
  const [editAway, setEditAway] = useState<'above' | 'below' | null>(null)
  const [nextChangeEdge, setNextChangeEdge] = useState<'above' | 'below' | null>(null)
  const [nextChangeIndex, setNextChangeIndex] = useState(0)
  const [editingHunk, setEditingHunk] = useState<string | null>(null)
  const [settling, setSettling] = useState<{
    fileId: string
    hunkId?: string
    kind: 'accepted' | 'rejected'
  } | null>(null)
  const [washOut, setWashOut] = useState<WashOut[]>([])
  const scrollerRef = useRef<HTMLDivElement>(null)
  const settleTimer = useRef<number | null>(null)
  const washTimer = useRef<number | null>(null)
  const scrollPin = useRef<ScrollPin | null>(null)
  const freezeScroll = useRef(false)
  const holdScroll = useRef(false)
  const heldTop = useRef(0)

  const batch = mode === 'batch'
  const file = FILES[batch ? fileIndex : 0]
  const fileDecision = fileDecisions[file.id] ?? 'pending'
  const blocks = useMemo(
    () => presentBlocks(file.blocks, file.id, fileDecision, hunkDecisions),
    [file.blocks, file.id, fileDecision, hunkDecisions],
  )
  const hunks = useMemo(() => hunkIdsInOrder(blocks), [blocks])
  const activeHunk = hunks[hunkIndex] ?? hunks[0]

  const requestScroll = (kind: 'hunk' | 'decoy') => {
    holdScroll.current = false
    setScrollIntent((prev) => ({ kind, nonce: prev.nonce + 1 }))
  }

  useLayoutEffect(() => {
    if (!holdScroll.current) return
    const node = scrollerRef.current
    if (node) node.scrollTop = heldTop.current
  })

  useLayoutEffect(() => {
    if (mode === 'compare' || freezeScroll.current) return
    const scroller = scrollerRef.current
    if (!scroller) return
    // Accept / Reject settles in place. Do not scroll to the decoy heading
    // once the washes are gone.
    if (scrollIntent.kind !== 'decoy' && !activeHunk) return
    const selector =
      scrollIntent.kind === 'decoy'
        ? '[data-diff-role="decoy"]'
        : activeHunk
          ? `[data-diff-hunk="${activeHunk}"][data-diff-scroll-target="true"]`
          : '[data-diff-role="decoy"]'
    const target = scroller.querySelector(selector)
    if (target instanceof HTMLElement) {
      scrollTargetIntoScroller(
        scroller,
        target,
        scrollIntent.kind === 'decoy' ? 'start' : 'center',
        scrollIntent.nonce > 0 ? 'smooth' : 'auto',
      )
    } else if (scrollIntent.kind === 'decoy') {
      scroller.scrollTo({ top: 0, behavior: 'auto' })
    }
  }, [mode, file.id, scrollIntent, activeHunk])

  useLayoutEffect(() => {
    const pin = scrollPin.current
    const scroller = scrollerRef.current
    if (!pin || !scroller) return
    const apply = () => {
      const current = scrollPin.current
      const node = scrollerRef.current
      if (!current || !node) return
      if (current.anchorId) {
        const anchor =
          node.querySelector(
            `[data-review-anchor="${current.anchorId}"] [contenteditable="true"]`,
          ) || node.querySelector(`[data-review-anchor="${current.anchorId}"]`)
        if (anchor instanceof HTMLElement) {
          const top = anchor.getBoundingClientRect().top - node.getBoundingClientRect().top
          const delta = top - current.anchorTop
          if (Math.abs(delta) > 0.5) node.scrollTop += delta
          return
        }
      }
      node.scrollTop = current.scrollTop
    }
    apply()
    const frame = window.requestAnimationFrame(apply)
    const follow = window.setTimeout(apply, 40)
    return () => {
      window.cancelAnimationFrame(frame)
      window.clearTimeout(follow)
    }
  })

  const pinScroll = (hunkId?: string) => {
    const scroller = scrollerRef.current
    if (!scroller) return
    const nodes = hunkId
      ? [...scroller.querySelectorAll(`[data-diff-hunk="${hunkId}"]`)]
      : []
    const focused = document.activeElement
    const focusedNode =
      focused instanceof HTMLElement && nodes.some((node) => node.contains(focused))
        ? focused
        : null
    const last = nodes[nodes.length - 1]
    const fallback =
      last instanceof HTMLElement
        ? last.querySelector('[contenteditable="true"]') ?? last
        : null
    const anchor = focusedNode ?? (fallback instanceof HTMLElement ? fallback : null)
    scrollPin.current = {
      anchorId: hunkId ?? '',
      anchorTop: anchor
        ? anchor.getBoundingClientRect().top - scroller.getBoundingClientRect().top
        : 0,
      scrollTop: scroller.scrollTop,
    }
  }

  useEffect(() => {
    if (mode === 'compare' || hunks.length === 0) {
      setEditAway(null)
      setNextChangeEdge(null)
      return
    }
    const scroller = scrollerRef.current
    if (!scroller) return
    const targets = hunks
      .map((id) => scroller.querySelector(`[data-diff-hunk="${id}"][data-diff-scroll-target="true"]`))
      .filter((node): node is HTMLElement => node instanceof HTMLElement)
    if (targets.length === 0) return
    const read = () => {
      const root = scroller.getBoundingClientRect()
      const rows = targets.map((node, index) => {
        const rect = node.getBoundingClientRect()
        const visible = rect.bottom > root.top + 12 && rect.top < root.bottom - 12
        return { index, visible, edge: rect.top < root.top ? 'above' as const : 'below' as const }
      })
      const current = rows[Math.min(hunkIndex, rows.length - 1)]
      if (current && !current.visible) {
        setEditAway(current.edge)
        setNextChangeEdge(null)
        return
      }
      setEditAway(null)
      const other = rows.find((row) => row.index !== current?.index && !row.visible)
      setNextChangeEdge(other ? other.edge : null)
      if (other) setNextChangeIndex(other.index)
    }
    read()
    const observer = new IntersectionObserver(read, { root: scroller, threshold: [0, 0.35, 1] })
    for (const node of targets) observer.observe(node)
    scroller.addEventListener('scroll', read, { passive: true })
    return () => {
      observer.disconnect()
      scroller.removeEventListener('scroll', read)
    }
  }, [mode, hunks, hunkIndex, file.id, fileDecision, hunkDecisions])

  useEffect(() => {
    if (mode === 'compare') return
    const scroller = scrollerRef.current
    if (!scroller) return
    const place = () => placeReviewChips(scroller)
    place()
    const observer = new ResizeObserver(place)
    observer.observe(scroller)
    scroller.addEventListener('scroll', place, { passive: true })
    return () => {
      observer.disconnect()
      scroller.removeEventListener('scroll', place)
    }
  }, [mode, file.id, fileDecision, hunkDecisions, hunks])

  useEffect(() => {
    return () => {
      if (settleTimer.current) window.clearTimeout(settleTimer.current)
      if (washTimer.current) window.clearTimeout(washTimer.current)
    }
  }, [])

  const review = (next: 'accepted' | 'rejected', hunkId?: string) => {
    if (settling) return
    if (mode === 'compare') {
      const previous = compareDecision
      setCompareDecision(next)
      reviewToast(next, 'Large rewrite', () => setCompareDecision(previous))
      return
    }
    const fileId = file.id
    const key = hunkId ? hunkKey(fileId, hunkId) : null
    const previous = key
      ? (hunkDecisions[key] ?? 'pending')
      : (fileDecisions[fileId] ?? 'pending')
    const previousHunks = key ? null : { ...hunkDecisions }
    const title = file.title
    const sourceBlocks = file.blocks
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const scroller = scrollerRef.current
    heldTop.current = scroller?.scrollTop ?? 0
    holdScroll.current = true
    if (scroller) scroller.scrollTo({ top: scroller.scrollTop, behavior: 'auto' })
    freezeScroll.current = true
    setSettling({ fileId, hunkId, kind: next })
    if (settleTimer.current) window.clearTimeout(settleTimer.current)
    settleTimer.current = window.setTimeout(() => {
      pinScroll(hunkId)
      const kept = washesFor(sourceBlocks, hunkId, next)
      if (key) {
        setHunkDecisions((prev) => ({ ...prev, [key]: next }))
      } else {
        setFileDecisions((prev) => ({ ...prev, [fileId]: next }))
      }
      setWashOut(kept)
      setSettling((current) => (current?.fileId === fileId ? null : current))
      if (washTimer.current) window.clearTimeout(washTimer.current)
      washTimer.current = window.setTimeout(() => {
        setWashOut([])
        scrollPin.current = null
        freezeScroll.current = false
      }, reduceMotion ? 0 : WASH_OUT_MS)
      reviewToast(next, title, () => {
        setWashOut([])
        scrollPin.current = null
        freezeScroll.current = false
        if (key) {
          setHunkDecisions((prev) => ({ ...prev, [key]: previous }))
          return
        }
        setFileDecisions((prev) => ({ ...prev, [fileId]: previous }))
        if (previousHunks) setHunkDecisions(previousHunks)
      })
    }, reduceMotion ? 0 : SETTLE_MS)
  }

  const beginEdit = (hunkId: string) => {
    setEditingHunk(hunkId)
    const index = hunks.indexOf(hunkId)
    if (index >= 0) setHunkIndex(index)
  }

  const endEdit = (hunkId: string) => {
    window.setTimeout(() => {
      const active = document.activeElement
      const nodes = scrollerRef.current?.querySelectorAll(`[data-diff-hunk="${hunkId}"]`)
      const stillInside =
        active instanceof Node &&
        [...(nodes ?? [])].some((node) => node.contains(active))
      if (stillInside) return
      setEditingHunk((current) => (current === hunkId ? null : current))
    }, 0)
  }

  const goToHunk = (next: number) => {
    if (hunks.length === 0) return
    const wrapped = (next + hunks.length) % hunks.length
    setHunkIndex(wrapped)
    requestScroll('hunk')
  }

  const goToFile = (next: number) => {
    const clamped = Math.min(FILES.length - 1, Math.max(0, next))
    setFileIndex(clamped)
    setHunkIndex(0)
    requestScroll('hunk')
  }

  const selectMode = (next: ReviewMode) => {
    setMode(next)
    if (next === 'document') setFileIndex(0)
    setHunkIndex(0)
    requestScroll('hunk')
  }

  return (
    <div className="space-y-4" data-testid={testIds.maaterUiGallery.diffReview}>
      {/* ?test= routes mount outside App's Toaster. */}
      <Toaster />
      <div className="max-w-3xl space-y-2 text-sm text-muted-foreground">
        <p>
          The note stays a note. Accept all on the review strip keeps every change.
          Hover an edit if you only want that one. Click the green wording to
          change it. Nothing here is written to the document.
        </p>
        <p>
          Source compare (Split / Unified, monospace) is opt-in for a large
          rewrite. Today that compare is a blocking modal titled &quot;Review
          proposed changes&quot;, and it is the primary path.
        </p>
        <p>
          Mapping hypothesis: a markdown line range paints the TipTap block that
          contains it. This scene is block-level only. A list-item or table-cell
          edit still lights the whole list or table. Character-level marks and a
          real line-to-node map are deferred. The Next steps list on Reid is one
          added block on purpose.
        </p>
        <p>
          Scroll away and a cue appears to bring you back. Production Review
          often stops about two paragraphs high (Alice, 2026-10-22).{' '}
          <button
            type="button"
            className="underline-offset-2 hover:underline"
            data-testid={testIds.maaterUiGallery.diffReviewMiss}
            onClick={() => requestScroll('decoy')}
          >
            Show where it used to stop.
          </button>
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-md border border-border p-0.5" role="tablist" aria-label="Diff review prototype">
          {MODES.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={mode === item.id}
              data-testid={testIds.maaterUiGallery.diffReviewMode(item.id)}
              className={cn(
                'rounded-sm px-2.5 py-1 text-xs',
                mode === item.id
                  ? 'bg-primary/10 font-medium text-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
              onClick={() => selectMode(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          onClick={() => {
            if (settleTimer.current) window.clearTimeout(settleTimer.current)
            if (washTimer.current) window.clearTimeout(washTimer.current)
            setSettling(null)
            setWashOut([])
            scrollPin.current = null
            freezeScroll.current = false
            toast.dismiss('diff-review-undo')
            setFileDecisions({})
            setHunkDecisions({})
            setCompareDecision('pending')
            setEditingHunk(null)
            setHunkIndex(0)
            setFileIndex(0)
            requestScroll('hunk')
          }}
        >
          Reset prototype
        </button>
      </div>

      <div className="flex h-[520px] flex-col overflow-hidden rounded-lg border border-border bg-card">
        <ProtoTitleRow
          density="inline"
          crumbs={[
            { id: 'people', label: file.crumb },
            { id: 'doc', label: mode === 'compare' ? 'Large rewrite' : file.title },
          ]}
          title={mode === 'compare' ? 'Large rewrite' : file.title}
          canGoBack={batch && fileIndex > 0}
          canGoForward={batch && fileIndex < FILES.length - 1}
          onBack={() => goToFile(fileIndex - 1)}
          onForward={() => goToFile(fileIndex + 1)}
          backTestId={testIds.maaterUiGallery.diffReviewFilePrev}
          forwardTestId={testIds.maaterUiGallery.diffReviewFileNext}
          testId={testIds.maaterUiGallery.diffReviewTitle}
          actions={
            mode === 'compare' ? (
              <ReviewActions
                label="Review this rewrite"
                acceptLabel="Accept"
                rejectLabel="Reject"
                disabled={Boolean(settling)}
                acceptTestId={testIds.maaterUiGallery.diffReviewAccept}
                rejectTestId={testIds.maaterUiGallery.diffReviewReject}
                onAccept={() => review('accepted')}
                onReject={() => review('rejected')}
              />
            ) : null
          }
        />
        {mode !== 'compare' && hunks.length > 0 ? (
          <div className="flex items-center gap-2 border-b border-primary/25 bg-primary/5 px-3 py-1.5">
            <span className="rounded-sm bg-primary/15 px-1.5 py-0.5 text-[11px] font-medium text-primary">
              Review
            </span>
            {hunks.length > 1 ? (
              <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground">
                <button
                  type="button"
                  className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-primary/10 hover:text-foreground"
                  aria-label="Previous edit"
                  onClick={() => goToHunk(hunkIndex - 1)}
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>
                <span className="tabular-nums">
                  {Math.min(hunkIndex, hunks.length - 1) + 1} of {hunks.length}
                </span>
                <button
                  type="button"
                  className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-primary/10 hover:text-foreground"
                  aria-label="Next edit"
                  onClick={() => goToHunk(hunkIndex + 1)}
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </span>
            ) : null}
            <div className="ml-auto">
              <ReviewActions
                label="Review the whole note"
                acceptLabel="Accept all"
                rejectLabel="Reject all"
                disabled={Boolean(settling)}
                acceptTestId={testIds.maaterUiGallery.diffReviewAccept}
                rejectTestId={testIds.maaterUiGallery.diffReviewReject}
                onAccept={() => review('accepted')}
                onReject={() => review('rejected')}
              />
            </div>
          </div>
        ) : null}
        <DecisionBanner decision={mode === 'compare' ? compareDecision : fileDecision} />

        {mode === 'compare' ? (
          <SourceCompare
            original={REWRITE_ORIGINAL}
            proposed={REWRITE_PROPOSED}
            filename="people/reid-patterson.md"
          />
        ) : (
          <div className="relative min-h-0 flex-1">
            <div
              ref={scrollerRef}
              className="h-full overflow-y-auto bg-background [overflow-anchor:none]"
              data-testid={testIds.maaterUiGallery.diffReviewReader}
            >
              <article className="document-reading-prose mx-auto box-border w-full max-w-[42rem] px-10 py-8">
                {chunkBlocks(blocks).map((chunk) => (
                  <div
                    key={chunk.id}
                    className={cn(chunk.hunkId && 'group/hunk relative')}
                  >
                    {chunk.blocks.map((block, index) => {
                      const changed = block.kind !== 'same'
                      const isScrollTarget = Boolean(chunk.hunkId && index === 0)
                      const mark = block.kind === 'add' ? '+' : block.kind === 'remove' ? '-' : ''
                      const hold =
                        settling?.fileId === file.id &&
                        (settling.hunkId == null || settling.hunkId === block.hunkId)
                      const leaving =
                        hold &&
                        ((settling.kind === 'accepted' && block.kind === 'remove') ||
                          (settling.kind === 'rejected' && block.kind === 'add'))
                      const fade = washOut.find((item) => item.id === block.id)
                      return (
                        <div
                          key={block.id}
                          className={cn('relative', index > 0 && 'mt-[0.75em]', leaving && 'diff-review-leave')}
                          data-review-anchor={fade?.hunkId}
                          data-diff-hunk={changed ? block.hunkId : undefined}
                          data-diff-scroll-target={isScrollTarget ? 'true' : undefined}
                          data-diff-role={block.decoy ? 'decoy' : undefined}
                          data-testid={
                            isScrollTarget && block.hunkId
                              ? testIds.maaterUiGallery.diffReviewHunk(block.hunkId)
                              : undefined
                          }
                        >
                          {mark ? (
                            <span
                              className={cn(
                                'diff-review-gutter select-none text-[11px] font-medium opacity-0 transition-opacity duration-200 group-hover/hunk:opacity-100',
                                block.kind === 'add'
                                  ? 'text-green-600 dark:text-green-400'
                                  : 'text-red-600 dark:text-red-400',
                              )}
                              data-testid={
                                block.hunkId && (block.kind === 'add' || block.kind === 'remove')
                                  ? testIds.maaterUiGallery.diffReviewHunkGutter(block.hunkId, block.kind)
                                  : undefined
                              }
                              aria-hidden
                            >
                              {mark}
                            </span>
                          ) : null}
                          <div
                            data-diff-kind={changed ? block.kind : undefined}
                            data-testid={
                              block.hunkId && (block.kind === 'add' || block.kind === 'remove')
                                ? testIds.maaterUiGallery.diffReviewHunkMark(block.hunkId, block.kind)
                                : undefined
                            }
                            className={cn(
                              (changed || fade) && 'diff-review-mark',
                              !hold &&
                                !fade &&
                                'transition-[background-color,border-color] duration-200 ease-out',
                              block.kind === 'add' &&
                                'cursor-text border-green-700/40 bg-green-600/[0.16] hover:border-green-700/55 hover:bg-green-600/[0.22] dark:border-green-400/75 dark:bg-green-500/[0.24] dark:hover:border-green-300 dark:hover:bg-green-500/[0.32]',
                              block.kind === 'remove' &&
                                'border-red-700/35 bg-red-600/[0.12] text-red-950/90 line-through decoration-red-700/45 hover:border-red-700/50 hover:bg-red-600/[0.18] dark:border-red-400/70 dark:bg-red-500/20 dark:text-red-100/90 dark:decoration-red-400/60 dark:hover:border-red-300 dark:hover:bg-red-500/[0.28]',
                              fade &&
                                (fade.tone === 'keep'
                                  ? 'diff-review-wash-out'
                                  : 'diff-review-wash-out-restore'),
                            )}
                          >
                            <BlockBody
                              block={block}
                              editable={block.kind !== 'remove'}
                              onFocus={
                                block.kind === 'add' && block.hunkId
                                  ? () => beginEdit(block.hunkId!)
                                  : undefined
                              }
                              onBlur={
                                block.kind === 'add' && block.hunkId
                                  ? () => endEdit(block.hunkId!)
                                  : undefined
                              }
                            />
                          </div>
                          {chunk.hunkId && (block.kind === 'add' || block.kind === 'remove') ? (
                            <div
                              className="diff-review-hunk-actions"
                              data-testid={testIds.maaterUiGallery.diffReviewHunkActions(
                                chunk.hunkId,
                                block.kind === 'add' ? 'add' : 'remove',
                              )}
                            >
                              <button
                                type="button"
                                disabled={Boolean(
                                  settling?.fileId === file.id &&
                                    (settling.hunkId == null || settling.hunkId === chunk.hunkId),
                                )}
                                data-testid={
                                  block.kind === 'add'
                                    ? testIds.maaterUiGallery.diffReviewHunkAccept(chunk.hunkId)
                                    : testIds.maaterUiGallery.diffReviewHunkReject(chunk.hunkId)
                                }
                                data-review-hover={block.kind === 'add' ? 'accept' : 'reject'}
                                className={cn(
                                  'inline-flex h-6 items-center gap-1 rounded-md border px-2 text-[12px] leading-none disabled:opacity-40',
                                  block.kind === 'add'
                                    ? 'diff-review-accept'
                                    : 'border-border/80 bg-background text-muted-foreground hover:bg-muted/70 hover:text-foreground',
                                )}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() =>
                                  review(block.kind === 'add' ? 'accepted' : 'rejected', chunk.hunkId)
                                }
                              >
                                {block.kind === 'add' ? <Check className="h-3 w-3" /> : null}
                                {block.kind === 'add' ? 'Accept' : 'Reject'}
                              </button>
                            </div>
                          ) : null}
                        </div>
                      )
                    })}
                  </div>
                ))}
              </article>
            </div>
            {editAway || nextChangeEdge ? (
              <button
                type="button"
                data-testid={testIds.maaterUiGallery.diffReviewLand}
                className={cn(
                  'absolute left-1/2 z-10 flex -translate-x-1/2 items-center gap-1 rounded-full border border-border/80 bg-background/90 px-3 py-1 text-xs text-foreground shadow-sm backdrop-blur-sm transition-colors duration-200 hover:bg-muted',
                  (editAway ?? nextChangeEdge) === 'above' ? 'top-3' : 'bottom-3',
                )}
                onClick={() => {
                  if (editAway) requestScroll('hunk')
                  else goToHunk(nextChangeIndex)
                }}
              >
                {(editAway ?? nextChangeEdge) === 'above' ? (
                  <ChevronUp className="size-3.5" />
                ) : (
                  <ChevronDown className="size-3.5" />
                )}
                {editAway
                  ? editAway === 'above'
                    ? 'Edit above'
                    : 'Edit below'
                  : 'Next change'}
              </button>
            ) : null}
          </div>
        )}

        <ListSurfaceStatusBar
          tips={TIPS}
          data-testid={testIds.maaterUiGallery.diffReviewStatus}
          helpTestId={testIds.maaterUiGallery.diffReviewHelp}
          leading={
            mode === 'compare' ? (
              <span className="text-muted-foreground">Source compare</span>
            ) : editingHunk ? (
              <span className="text-foreground">Editing</span>
            ) : null
          }
          trailing={
            <span className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
              {batch ? <span>{`${fileIndex + 1} of ${FILES.length}`}</span> : null}
              {mode === 'compare' ? <span>people/reid-patterson.md</span> : null}
            </span>
          }
        />
      </div>
    </div>
  )
}
